import clsx from "clsx";
import { orderBy } from "lodash";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import {
  ColorWarning,
  IgDivider,
  IgTypography,
  WarningIcon,
} from "@intelligems/ig-theme";
import { UserOrganizationListDto } from "@intelligems/ig-types";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Autocomplete,
  Box,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Popover,
  Radio,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useGetOrg } from "../../api/organizations/get-organization";
import { useSwitchOrganization } from "../../api/organizations/switch-organization";
import { useLoggedInUser } from "../../api/user-auths/login";
import { IgHorizWhiteLogo } from "../../images/Icons/IgHorizWhiteLogo";
import { GENERAL_ROUTES } from "../../routes/routes";
import { getAuthToken } from "../../utils";

interface OrgPickerProps {
  redirectToHome?: boolean;
}

const PREFIX = "OrgPicker";

const classes = {
  orgName: `${PREFIX}-orgName`,
  headerContainer: `${PREFIX}-headerContainer`,
  expandMoreIcon: `${PREFIX}-expandMoreIcon`,
  textfield: `${PREFIX}-textfield`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.orgName}`]: {
    fontSize: "14px",
    fontWeight: 500,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    color: "white",
    width: "148px",
  },
  [`& .${classes.expandMoreIcon}`]: {
    marginLeft: "-5px",
    color: "white",
    marginTop: "34px",
  },
  [`& .${classes.textfield}`]: {
    "& .MuiInputBase-input": {
      color: "white",
      fontSize: "14px",
      "&:hover": { cursor: "pointer" },
    },
    "& .MuiInput-underline:after": {
      border: "1px solid white",
    },
    "& .MuiInput-underline:before": { border: "none" },
    "& .MuiAutocomplete-popupIndicator": {
      color: "white",
    },
  },

  display: "flex",
  flexDirection: "column",
  padding: "6px",
  marginTop: "-60px",
  marginLeft: "4px",
}));

export const OrgPicker = ({ redirectToHome }: OrgPickerProps) => {
  const [nameBy, setNameBy] = useImmer<"name" | "shopId">("name");
  const [order, setOrder] = useImmer<"name" | "date">("name");

  const user = useLoggedInUser();
  const organization = useGetOrg();
  const navigate = useNavigate();
  const switchOrganization = useSwitchOrganization();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleSwitchOrg = async (orgId: string) => {
    switchOrganization.mutate({
      token: getAuthToken() || "",
      orgId: orgId,
    });
  };

  return (
    <StyledBox
      onClick={() => {
        redirectToHome
          ? navigate(GENERAL_ROUTES.Home)
          : window.location.pathname === GENERAL_ROUTES.Home
            ? null
            : navigate(GENERAL_ROUTES.Home);
      }}
      sx={{
        cursor: redirectToHome
          ? "pointer"
          : window.location.pathname === GENERAL_ROUTES.Home
            ? "auto"
            : "pointer",
      }}
    >
      <Grid container direction={"column"}>
        <Grid item sx={{ marginLeft: "auto", marginRight: "auto" }}>
          <IgHorizWhiteLogo sx={{ fontSize: "143px" }} />
        </Grid>
        <Grid container item sx={{ marginTop: "-30px" }}>
          {user.isSuccess && user.data.user?.organizations ? (
            user.data.user.organizations.length > 1 && !redirectToHome ? (
              <Grid
                container
                item
                flexWrap={"nowrap"}
                alignItems={"start"}
                spacing={1}
              >
                <Grid item>
                  <IconButton
                    size={"small"}
                    sx={{ color: "white", padding: "0px 0px 2px 0px" }}
                    onClick={handleClick}
                  >
                    <FilterListIcon sx={{ fontSize: "18px" }} />
                  </IconButton>
                  <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    slotProps={{
                      paper: {
                        sx: {
                          width: "180px",
                          padding: "10px",
                        },
                      },
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                  >
                    <Grid container item direction={"column"}>
                      <Grid item>
                        <IgTypography sx={{ fontSize: "14px" }}>
                          Display
                        </IgTypography>
                      </Grid>
                      <Grid item>
                        <FormControlLabel
                          value="beatControl"
                          sx={{
                            "& .MuiTypography-root": { fontSize: "13px" },
                          }}
                          control={
                            <Radio
                              sx={{ height: "24px" }}
                              disableRipple={true}
                              checked={nameBy === "name"}
                              size={"small"}
                              onClick={() => {
                                setNameBy("name");
                              }}
                            />
                          }
                          label="Name"
                        />
                      </Grid>
                      <Grid item>
                        <FormControlLabel
                          value="beatControl"
                          sx={{
                            "& .MuiTypography-root": { fontSize: "13px" },
                          }}
                          control={
                            <Radio
                              sx={{ height: "24px" }}
                              disableRipple={true}
                              size={"small"}
                              checked={nameBy === "shopId"}
                              onClick={() => {
                                setNameBy("shopId");
                              }}
                            />
                          }
                          label="Shopify Domain"
                        />
                      </Grid>
                      <Grid item sx={{ marginTop: "10px" }}>
                        <Divider />
                      </Grid>
                      <Grid item sx={{ marginTop: "5px" }}>
                        <IgTypography sx={{ fontSize: "14px" }}>
                          Sort
                        </IgTypography>
                      </Grid>
                      <Grid item>
                        <FormControlLabel
                          value="beatControl"
                          sx={{
                            "& .MuiTypography-root": { fontSize: "13px" },
                          }}
                          control={
                            <Radio
                              sx={{ height: "24px" }}
                              disableRipple={true}
                              size={"small"}
                              checked={order === "name"}
                              onClick={() => {
                                setOrder("name");
                              }}
                            />
                          }
                          label="A to Z"
                        />
                      </Grid>
                      <Grid item>
                        <FormControlLabel
                          value="beatControl"
                          sx={{
                            "& .MuiTypography-root": { fontSize: "13px" },
                          }}
                          control={
                            <Radio
                              sx={{ height: "24px" }}
                              disableRipple={true}
                              size={"small"}
                              checked={order === "date"}
                              onClick={() => {
                                setOrder("date");
                              }}
                            />
                          }
                          label="By Install Date"
                        />
                      </Grid>
                    </Grid>
                  </Popover>
                </Grid>
                <Grid item>
                  <Autocomplete
                    onChange={(e, newValue) => {
                      void handleSwitchOrg(newValue.orgId);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.orgId === value.orgId
                    }
                    disableClearable={true}
                    componentsProps={{
                      paper: {
                        sx: {
                          "&.MuiPaper-root": {
                            width: "275px",
                          },
                        },
                      },
                    }}
                    value={{
                      orgId: organization.data.org.id,
                      orgName: organization.data.org.name,
                      isInstalled: organization.data["isInstalled"] || true,
                      shopId: organization.data.org.shopId!,
                      createdAtTs: organization.data.org.createdAtTs || 0,
                    }}
                    options={orderBy(
                      user.data.user.organizations,
                      order === "name" ? ["name"] : ["createdAtTs"],
                      order === "name" ? ["asc"] : ["desc"]
                    ).map((org: UserOrganizationListDto) => ({
                      orgId: org.id,
                      orgName: org.name,
                      shopId: org.shopId,
                      isInstalled: org.isInstalled,
                      createdAtTs: org.createdAtTs,
                    }))}
                    getOptionLabel={(option) =>
                      nameBy === "name"
                        ? option.orgName
                        : option.shopId.replace(".myshopify.com", "")
                    }
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        onClick={() => {
                          void handleSwitchOrg(option.orgId);
                        }}
                        key={option.orgId}
                      >
                        <Grid container columnSpacing={1} alignItems={"center"}>
                          <Grid item>
                            <IgTypography sx={{ fontSize: "14px" }}>
                              {nameBy === "name"
                                ? option.orgName
                                : option.shopId.replace(".myshopify.com", "")}
                            </IgTypography>
                          </Grid>
                          {!option.isInstalled ? (
                            <Grid item sx={{ marginTop: "8px" }}>
                              <WarningIcon
                                fill={ColorWarning.main}
                                height={"16px"}
                                width={"16px"}
                              />
                            </Grid>
                          ) : null}
                        </Grid>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className={clsx(classes.orgName, classes.textfield)}
                        variant={"standard"}
                      />
                    )}
                    filterOptions={(options, state) => {
                      return options.filter((option) => {
                        const matchesName = option.orgName
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase());
                        const matchesId = option.orgId
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase());

                        const matchesShopId = option.shopId
                          ?.toLowerCase()
                          .includes(state.inputValue.toLowerCase());

                        if (state.inputValue.length <= 4) {
                          return matchesName;
                        }
                        return matchesName || matchesId || matchesShopId;
                      });
                    }}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container item justifyContent={"center"}>
                <Grid item>
                  <IgTypography className={classes.orgName}>
                    {organization.data.org.name}
                  </IgTypography>
                </Grid>
              </Grid>
            )
          ) : null}
          <IgDivider spacing={"s"} color={"white"} />
        </Grid>
      </Grid>
    </StyledBox>
  );
};
